import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CreateEndClientReportRequest, CreateEndClientReportTemplateRequest, CreateEndClientReportThemeRequest, DesignChatFailure, DesignChatTurn, DesignChatViewing, DesignHistoryEntry, DesignRegenerateFailure, DesignSaveFailure, EndClientReport, EndClientReportDataSource, EndClientReportDataSourcePreflight, EndClientReportDataSourceProvider, EndClientReportDataSourceUsage, EndClientReportDeployment, EndClientReportDomain, EndClientReportUploadFile, EndClientReportTemplate, EndClientReportTheme, EndClientReportThemeApplyResult, EndClientReportThemeUsage, ReportDesign, ThemeChoice, ThemeDocument, ThemeSaveFailure } from '../../models/end-client-reporting-package';
import { DecipherDatamap, RespondentRecord, SurveyLayout, parseDatamap } from './report-aggregation';
import { environment } from '../../environments/environment';
import { withCurrentReportTypes } from './report-type-aliases';
import { ReportDeckCache } from './report-deck-cache.service';

/** Everything the deck reads to draw a report. */
export interface DeckLoad {
  report: EndClientReport;
  design: ReportDesign;
  records: RespondentRecord[];
  layout: SurveyLayout;
}

const SAVE_FAILURES: readonly DesignSaveFailure[] = ['Busy', 'VersionConflict', 'Invalid', 'ArchiveFailed'];

// Prefer the API's own `reason` over the status code -- it's the field the backend maintains
// alongside DesignSaveResult, so it stays right if a status ever changes. Status is the fallback for
// the cases that never reach the controller's switch (a gateway 503, an offline request).
function designSaveFailure(response: HttpErrorResponse): DesignSaveFailure {
  const reason = response.error?.reason as DesignSaveFailure | undefined;
  if (reason && SAVE_FAILURES.includes(reason)) return reason;

  switch (response.status) {
    case 409: return 'VersionConflict';
    case 423: return 'Busy';
    case 422: return 'Invalid';
    default: return 'Unknown';
  }
}

const REGENERATE_FAILURES: readonly DesignRegenerateFailure[] = ['AlreadyGenerated', 'AttemptInFlight', 'LayoutNotReady'];

// All three refusals share a 409, so the status alone can't tell them apart -- the API's own
// `reason` is the only thing that can, with the status as the fallback for responses that never
// reached the controller.
function designRegenerateFailure(response: HttpErrorResponse): DesignRegenerateFailure {
  const reason = response.error?.reason as DesignRegenerateFailure | undefined;
  if (reason && REGENERATE_FAILURES.includes(reason)) return reason;
  return response.status === 404 ? 'NotFound' : 'Unknown';
}

const CHAT_FAILURES: readonly DesignChatFailure[] = ['Rejected', 'Busy', 'VersionConflict', 'AgentFailed', 'ArchiveFailed'];

// Same shape as designSaveFailure: trust the API's own `reason` first, fall back to the status for
// the responses that never reached the controller's switch.
function designChatFailure(response: HttpErrorResponse): DesignChatFailure {
  const reason = response.error?.reason as DesignChatFailure | undefined;
  if (reason && CHAT_FAILURES.includes(reason)) return reason;

  switch (response.status) {
    case 409: return 'VersionConflict';
    case 422: return 'Rejected';
    case 423: return 'Busy';
    case 502: return 'AgentFailed';
    default: return 'Unknown';
  }
}

const THEME_SAVE_FAILURES: readonly ThemeSaveFailure[] = ['Invalid', 'NameTaken'];

// Same shape again: the API's `reason` first, the status as the fallback.
function themeSaveFailure(response: HttpErrorResponse): ThemeSaveFailure {
  const reason = response.error?.reason as ThemeSaveFailure | undefined;
  if (reason && THEME_SAVE_FAILURES.includes(reason)) return reason;

  switch (response.status) {
    case 404: return 'NotFound';
    case 409: return 'NameTaken';
    case 422: return 'Invalid';
    default: return 'Unknown';
  }
}

@Injectable({ providedIn: 'root' })
export class EndClientReportService {
  baseUrl = environment.apiEndpoint + '/endclientreport/';
  templateUrl = environment.apiEndpoint + '/endclientreporttemplate/';
  /** Admin-only (NK-399): managing the hostnames a shared report can be served on. */
  domainUrl = environment.apiEndpoint + '/endclientreportdomain/';
  /** Publishing a report as a share link (NK-399). Not admin-gated, unlike the domain list above. */
  sharingUrl = environment.apiEndpoint + '/endclientreportsharing/';
  /** Named report colours, owned by a client (NK-450). */
  themeUrl = environment.apiEndpoint + '/endclientreporttheme/';

  private deckCache = inject(ReportDeckCache);

  constructor(private http: HttpClient) { }

  /**
   * The report row first, since it says which versions of the rest are current; then the design and
   * the source's data and layout through the browser cache. An extra round trip when nothing is
   * cached, against the full data download saved when it is.
   */
  loadDeck(reportId: string): Observable<DeckLoad> {
    return this.getReport(reportId).pipe(
      switchMap(report => {
        const version = report.reportDesignVersion;
        const sourceId = report.dataSourceId;
        const fetchedAt = report.dataFetchedAt;
        const design$ = version
          ? this.deckCache.get(`design:${reportId}:${version}`, () => this.getReportDesign(reportId))
          : this.getReportDesign(reportId);
        const records$ = sourceId && fetchedAt
          ? this.deckCache.get(`records:${sourceId}:${fetchedAt}`, () => this.getRecords(reportId), sourceId)
          : this.getRecords(reportId);
        const layout$ = sourceId && fetchedAt
          ? this.deckCache.get(`layout:${sourceId}:${fetchedAt}`, () => this.getSurveyLayout(reportId), sourceId)
          : this.getSurveyLayout(reportId);
        return forkJoin({ design: design$, records: records$, layout: layout$ }).pipe(
          map(rest => ({ report, ...rest })));
      }),
    );
  }

  /** The same four reads with no cache, for the services that stand in for this one. */
  protected loadDeckUncached(reportId: string): Observable<DeckLoad> {
    return forkJoin({
      report: this.getReport(reportId),
      design: this.getReportDesign(reportId),
      records: this.getRecords(reportId),
      layout: this.getSurveyLayout(reportId),
    });
  }

  getReportsForProject(projectId: string): Observable<EndClientReport[]> {
    return this.http.get<EndClientReport[]>(this.baseUrl + 'project/' + projectId, { headers: { cache: '0' } });
  }

  getReport(id: string): Observable<EndClientReport> {
    return this.http.get<EndClientReport>(this.baseUrl + id);
  }

  getReportDesign(reportId: string): Observable<ReportDesign> {
    return this.http.get<ReportDesign>(this.baseUrl + reportId + '/design').pipe(map(withCurrentReportTypes));
  }

  getRecords(reportId: string): Observable<RespondentRecord[]> {
    return this.http.get<RespondentRecord[]>(this.baseUrl + reportId + '/data');
  }

  getSurveyLayout(reportId: string): Observable<SurveyLayout> {
    return this.http.get<DecipherDatamap>(this.baseUrl + reportId + '/layout').pipe(map(parseDatamap));
  }

  /** The source's datamap as Decipher wrote it, for a data download: parsing drops what a deck can't chart. */
  getDatamap(reportId: string): Observable<DecipherDatamap> {
    return this.http.get<DecipherDatamap>(this.baseUrl + reportId + '/layout');
  }

  /**
   * The creative behind an image-highlighter question, as copied from the survey when its data was
   * pulled. Null where there is none: a heatmap without one is a normal state, not a failure.
   */
  getQuestionImage(reportId: string, questionLabel: string): Observable<Blob | null> {
    return this.imageAt(`${this.baseUrl}${reportId}/heatmap-image/question/${encodeURIComponent(questionLabel)}`);
  }

  /** An image uploaded onto one of this report's heatmaps. Null where it has gone. */
  getUploadedImage(reportId: string, imageId: string): Observable<Blob | null> {
    return this.imageAt(`${this.baseUrl}${reportId}/heatmap-image/${encodeURIComponent(imageId)}`);
  }

  /** Stores an image for one of this report's heatmaps; the block names it by the id returned. */
  uploadHeatmapImage(reportId: string, file: File): Observable<string> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<{ imageId: string }>(`${this.baseUrl}${reportId}/heatmap-image`, body)
      .pipe(map(response => response.imageId));
  }

  /** A picture kept with a template -- copied from the report it was saved from. Null where it has gone. */
  getTemplateImage(templateId: string, imageId: string): Observable<Blob | null> {
    return this.imageAt(`${this.templateUrl}${templateId}/image/${encodeURIComponent(imageId)}`);
  }

  /** A theme logo (NK-518), by id: the theme editor's preview, and a deck's top bar. Null where it has gone. */
  getLogo(imageId: string): Observable<Blob | null> {
    return this.imageAt(`${this.themeUrl}logo/${encodeURIComponent(imageId)}`);
  }

  /** Stores a theme logo; the theme document names it by the id returned, and is saved as usual. */
  uploadThemeLogo(file: File): Observable<string> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<{ imageId: string }>(`${this.themeUrl}logo`, body)
      .pipe(map(response => response.imageId));
  }

  private imageAt(url: string): Observable<Blob | null> {
    return this.http.get(url, { responseType: 'blob' }).pipe(catchError(() => of(null)));
  }

  createReport(projectId: string, model: CreateEndClientReportRequest): Observable<EndClientReport> {
    return this.http.post<EndClientReport>(this.baseUrl + projectId, model);
  }

  updateReport(reportId: string, name: string, description?: string): Observable<EndClientReport> {
    return this.http.put<EndClientReport>(this.baseUrl + reportId, { name, description });
  }

  /**
   * Saves the whole design document. `baseVersion` is the report's current reportDesignVersion --
   * the API rejects the save if it has moved on, rather than letting this overwrite an edit made
   * since this copy was loaded. Returns the refreshed report, whose reportDesignVersion is what the
   * *next* save has to send.
   *
   * Errors surface as a DesignSaveFailure rather than an HttpErrorResponse, because every caller
   * needs the distinction between them (retry vs reload vs give up) and none needs the status code.
   */
  /**
   * `theme`, when the edit changed the deck's theme (NK-450), sets the theme columns in the same
   * request. Leave it out otherwise -- absent means "unchanged", not "OR Default".
   */
  saveReportDesign(reportId: string, design: ReportDesign, baseVersion: number, theme?: ThemeChoice): Observable<EndClientReport> {
    return this.http.put<EndClientReport>(this.baseUrl + reportId + '/design', { design, baseVersion, theme })
      .pipe(
        // The list's thumbnail of this report then draws the saved version without fetching it.
        tap(saved => {
          if (saved?.reportDesignVersion) this.deckCache.put(`design:${reportId}:${saved.reportDesignVersion}`, design);
        }),
        catchError((response: HttpErrorResponse) => throwError(() => designSaveFailure(response))));
  }

  /**
   * One turn of the AI chat edit. The design isn't sent -- the API reads the current document
   * itself, so the agent always edits what's actually saved rather than this browser's copy.
   * `baseVersion` is what catches the case where those two have diverged: a stale one is rejected
   * rather than quietly editing a deck the user can no longer see.
   *
   * A resolved turn hasn't necessarily changed anything (see DesignChatTurn.designChanged) -- the
   * agent replies through this same call when it answers a question or needs clarification.
   */
  sendDesignChat(reportId: string, message: string, sessionId: string | null, baseVersion: number,
                 viewing: DesignChatViewing | null = null): Observable<DesignChatTurn> {
    return this.http.post<DesignChatTurn>(this.baseUrl + reportId + '/design/chat', { message, sessionId, baseVersion, viewing })
      .pipe(catchError((response: HttpErrorResponse) => throwError(() => designChatFailure(response))));
  }

  /**
   * Asks for a stuck deck's design to be generated again. Only offered when the report's own
   * `canRegenerate` is true — the API enforces the same rules regardless, since a hidden button
   * isn't a guard.
   *
   * Resolving means an attempt ran, not that it produced a design: re-read the report to find out.
   * A rejection is one of the `DesignRegenerateFailure` cases, all of which mean "your view was out
   * of date" rather than something being broken.
   */
  regenerateDesign(reportId: string): Observable<void> {
    return this.http.post<void>(this.baseUrl + reportId + '/regenerate', {})
      .pipe(catchError((response: HttpErrorResponse) => throwError(() => designRegenerateFailure(response))));
  }

  /**
   * A deck's design history, newest first. Internal users only server-side.
   *
   * Empty for a deck whose versions predate history being recorded — those list as nothing rather
   * than as rows with no author, time or reason, which is the useless picker this replaces.
   */
  getDesignHistory(reportId: string): Observable<DesignHistoryEntry[]> {
    return this.http.get<DesignHistoryEntry[]>(this.baseUrl + reportId + '/design/history');
  }

  /** One version's design document — the archive for an earlier version, the live one for the current. */
  getDesignVersion(reportId: string, version: number): Observable<ReportDesign> {
    return this.http.get<ReportDesign>(`${this.baseUrl}${reportId}/design/versions/${version}`)
      .pipe(map(withCurrentReportTypes));
  }

  /**
   * Restores an earlier version by saving its content as a **new** version, so the revert is its own
   * entry in the history rather than a gap in it. Fails the same ways any other save does, which is
   * the point of it going through the same path.
   */
  revertDesign(reportId: string, version: number): Observable<EndClientReport> {
    return this.http.post<EndClientReport>(
      `${this.baseUrl}${reportId}/design/versions/${version}/revert`, {})
      .pipe(catchError((response: HttpErrorResponse) => throwError(() => designSaveFailure(response))));
  }

  // ---------------------------------------------------------------------------------------
  // Templates
  //
  // A separate controller, but the deck components don't see that: they're handed a design and a
  // save function, and the branch lives here.
  // ---------------------------------------------------------------------------------------

  /**
   * The survey a template is edited against: its own source project's real layout and data.
   *
   * This replaced a shared demo fixture (2026-09-02). The fixture reasoning — that a template is
   * reused across studies, so previewing it against one project implies a binding that doesn't
   * exist — had it backwards. A template's `variableMap` already holds one project's data
   * positions, so that project is the only survey it resolves against; any other one renders empty
   * charts, which is exactly what templates saved from a real deck were doing.
   *
   * Addressed by template rather than by project: the server resolves which project and scopes it,
   * so these can't be used to reach a study the caller can't see. A 404 means the source project
   * couldn't be loaded — gone, never recorded, or not theirs — and the editor sends the user back
   * to the list rather than rendering against nothing.
   */
  getTemplateRecords(templateId: string): Observable<RespondentRecord[]> {
    return this.http.get<RespondentRecord[]>(this.templateUrl + templateId + '/data');
  }

  getTemplateSurveyLayout(templateId: string): Observable<SurveyLayout> {
    return this.http.get<DecipherDatamap>(this.templateUrl + templateId + '/layout').pipe(map(parseDatamap));
  }

  /**
   * Templates the caller may see: their own client's, or all of them for an internal user.
   *
   * `clientId` narrows that to one client and is filtered server-side, so another client's template
   * names never reach the browser. It can only subtract — a client user's own scope wins regardless,
   * and naming someone else's client is refused rather than silently ignored.
   */
  getTemplates(clientId?: string): Observable<EndClientReportTemplate[]> {
    const params = clientId ? { params: { clientId } } : {};
    return this.http.get<EndClientReportTemplate[]>(this.templateUrl, params);
  }

  getTemplate(templateId: string): Observable<EndClientReportTemplate> {
    return this.http.get<EndClientReportTemplate>(this.templateUrl + templateId);
  }

  /** The template's design document. Always present -- a template is created with content. */
  getTemplateContent(templateId: string): Observable<ReportDesign> {
    return this.http.get<ReportDesign>(this.templateUrl + templateId + '/content').pipe(map(withCurrentReportTypes));
  }

  createTemplate(model: CreateEndClientReportTemplateRequest): Observable<EndClientReportTemplate> {
    return this.http.post<EndClientReportTemplate>(this.templateUrl, model);
  }

  updateTemplate(templateId: string, name: string, description?: string): Observable<EndClientReportTemplate> {
    return this.http.put<EndClientReportTemplate>(this.templateUrl + templateId, { name, description });
  }

  // reassignTemplate was removed 2026-09-02 -- a template is edited against its source project's
  // real survey and data, so it can't change client without changing which study it shows. See the
  // note in EndClientReportTemplateController.

  cloneTemplate(templateId: string): Observable<EndClientReportTemplate> {
    return this.http.post<EndClientReportTemplate>(this.templateUrl + templateId + '/clone', {});
  }

  deleteTemplate(templateId: string): Observable<void> {
    return this.http.delete<void>(this.templateUrl + templateId);
  }

  /**
   * Saves a template's design. Same contract as `saveReportDesign` -- `baseVersion` is the version
   * the edit was based on, and a stale one is rejected rather than overwriting someone else's work
   * -- so the deck's save handling is shared between the two.
   */
  saveTemplateContent(templateId: string, design: ReportDesign, baseVersion: number, theme?: ThemeChoice): Observable<EndClientReportTemplate> {
    return this.http.put<EndClientReportTemplate>(
      this.templateUrl + templateId + '/content', { template: design, baseVersion, theme })
      .pipe(catchError((response: HttpErrorResponse) => throwError(() => designSaveFailure(response))));
  }

  /** A template's history, newest first (NK-450). Same contract as `getDesignHistory`. */
  getTemplateHistory(templateId: string): Observable<DesignHistoryEntry[]> {
    return this.http.get<DesignHistoryEntry[]>(this.templateUrl + templateId + '/content/history');
  }

  getTemplateVersion(templateId: string, version: number): Observable<ReportDesign> {
    return this.http.get<ReportDesign>(`${this.templateUrl}${templateId}/content/versions/${version}`)
      .pipe(map(withCurrentReportTypes));
  }

  /** Same as `revertDesign`, for a template: a new version holding the old content, theme included. */
  revertTemplate(templateId: string, version: number): Observable<EndClientReportTemplate> {
    return this.http.post<EndClientReportTemplate>(
      `${this.templateUrl}${templateId}/content/versions/${version}/revert`, {})
      .pipe(catchError((response: HttpErrorResponse) => throwError(() => designSaveFailure(response))));
  }

  // ---------------------------------------------------------------------------------------
  // Themes (NK-450)
  // ---------------------------------------------------------------------------------------

  /**
   * OR Default first, then one client's themes by name. Name the client, or a project to have the
   * server resolve its client -- the deck editor knows its project but not its client.
   */
  getThemes(scope: { clientId?: string; projectId?: string }): Observable<EndClientReportTheme[]> {
    const params: Record<string, string> = {};
    if (scope.clientId) params['clientId'] = scope.clientId;
    else if (scope.projectId) params['projectId'] = scope.projectId;
    return this.http.get<EndClientReportTheme[]>(this.themeUrl, { params, headers: { cache: '0' } });
  }

  getDefaultTheme(): Observable<EndClientReportTheme> {
    return this.http.get<EndClientReportTheme>(this.themeUrl + 'defaults');
  }

  getTheme(themeId: string): Observable<EndClientReportTheme> {
    return this.http.get<EndClientReportTheme>(this.themeUrl + themeId, { headers: { cache: '0' } });
  }

  /** Rejects with a ThemeSaveFailure. */
  createTheme(model: CreateEndClientReportThemeRequest): Observable<EndClientReportTheme> {
    return this.http.post<EndClientReportTheme>(this.themeUrl, model)
      .pipe(catchError((response: HttpErrorResponse) => throwError(() => themeSaveFailure(response))));
  }

  /**
   * Saves name, description and colours together. The returned themeVersion has gone up only if
   * the colours changed, which is how the editor knows whether there's an update to push.
   */
  updateTheme(themeId: string, name: string, description: string | undefined, theme: ThemeDocument): Observable<EndClientReportTheme> {
    return this.http.put<EndClientReportTheme>(this.themeUrl + themeId, { name, description, theme })
      .pipe(catchError((response: HttpErrorResponse) => throwError(() => themeSaveFailure(response))));
  }

  cloneTheme(themeId: string): Observable<EndClientReportTheme> {
    return this.http.post<EndClientReportTheme>(this.themeUrl + themeId + '/clone', {});
  }

  /** Refused with 409 while any report or template uses the theme. */
  deleteTheme(themeId: string): Observable<void> {
    return this.http.delete<void>(this.themeUrl + themeId);
  }

  getThemeUsage(themeId: string): Observable<EndClientReportThemeUsage[]> {
    return this.http.get<EndClientReportThemeUsage[]>(this.themeUrl + themeId + '/usage', { headers: { cache: '0' } });
  }

  /**
   * Pushes the theme's current colours into the reports and templates named, each as its own new
   * design version. Resolves with how each one went -- a partial result is normal, not an error.
   *
   * `republishReportIds` are rebuilt from their updated design afterwards, which sends everything
   * changed since they were last published, not just the colours.
   */
  applyTheme(
    themeId: string, reportIds: string[], templateIds: string[], republishReportIds: string[] = [],
  ): Observable<EndClientReportThemeApplyResult[]> {
    return this.http.post<EndClientReportThemeApplyResult[]>(
      this.themeUrl + themeId + '/apply', { reportIds, templateIds, republishReportIds });
  }

  deleteReport(reportId: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + reportId);
  }

  /**
   * Turns an uploaded export into a data source of its own. Returns as soon as the row exists --
   * the conversion runs behind it, and the source's own status carries the outcome, so the caller
   * polls it exactly as it would a refresh.
   *
   * Files are a list rather than one argument because a CSV upload will carry its layout as a
   * second file; the API binds each by its field name.
   */
  uploadDataSource(
    name: string,
    provider: EndClientReportDataSourceProvider,
    files: EndClientReportUploadFile[],
    projectId: string,
  ): Observable<EndClientReportDataSource> {
    // The project owns the upload; it stays that project's until someone shares it.
    const body = new FormData();
    body.append('name', name);
    body.append('provider', String(provider));
    body.append('projectId', projectId);
    for (const upload of files) body.append(upload.field, upload.file, upload.file.name);

    return this.http.post<EndClientReportDataSource>(this.baseUrl + 'datasource/upload', body);
  }

  /**
   * What this report would lose by reading that source instead -- asked before switching, since
   * afterwards a mismatch looks like a broken deck rather than the wrong survey.
   */
  preflightDataSource(reportId: string, dataSourceId: string): Observable<EndClientReportDataSourcePreflight> {
    return this.http.get<EndClientReportDataSourcePreflight>(
      this.baseUrl + reportId + '/datasource/' + dataSourceId + '/preflight', { headers: { cache: '0' } });
  }

  /** The project's own sources plus what its reports read, each with a live-report count. */
  /** `poll` marks a re-read while something refreshes, which the API answers without writing anything. */
  getDataSourcesForManagement(projectId: string, poll = false): Observable<EndClientReportDataSource[]> {
    return this.http.get<EndClientReportDataSource[]>(
      this.baseUrl + 'project/' + projectId + '/datasources/manage' + (poll ? '?poll=true' : ''), { headers: { cache: '0' } });
  }

  /** The shared sources of this project's client, each with its owner project's name. */
  getSharedDataSources(projectId: string): Observable<EndClientReportDataSource[]> {
    return this.http.get<EndClientReportDataSource[]>(
      this.baseUrl + 'project/' + projectId + '/datasources/shared', { headers: { cache: '0' } });
  }

  /** Refused with 409 when unsharing while reports in other projects read it; the body says how many. */
  setDataSourceShared(dataSourceId: string, isGlobal: boolean): Observable<EndClientReportDataSource> {
    return this.http.put<EndClientReportDataSource>(this.baseUrl + 'datasource/' + dataSourceId + '/share', { isGlobal });
  }

  renameDataSource(dataSourceId: string, name: string): Observable<EndClientReportDataSource> {
    return this.http.put<EndClientReportDataSource>(this.baseUrl + 'datasource/' + dataSourceId, { name });
  }

  /** Refused with 409 while any live report reads it; the body carries how many. */
  deleteDataSource(dataSourceId: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + 'datasource/' + dataSourceId)
      .pipe(tap(() => this.deckCache.forgetSource(dataSourceId)));
  }

  /** New data for an existing uploaded source -- the file equivalent of refreshing a pull. */
  reuploadDataSource(
    dataSourceId: string,
    provider: EndClientReportDataSourceProvider,
    files: EndClientReportUploadFile[],
  ): Observable<EndClientReportDataSource> {
    const body = new FormData();
    body.append('provider', String(provider));
    for (const upload of files) body.append(upload.field, upload.file, upload.file.name);

    return this.http.post<EndClientReportDataSource>(
      this.baseUrl + 'datasource/' + dataSourceId + '/reupload', body)
      .pipe(tap(() => this.deckCache.forgetSource(dataSourceId)));
  }

  /** Every report reading a source -- what a refresh of it would change. */
  getReportsUsingDataSource(dataSourceId: string): Observable<EndClientReportDataSourceUsage[]> {
    return this.http.get<EndClientReportDataSourceUsage[]>(
      this.baseUrl + 'datasource/' + dataSourceId + '/reports', { headers: { cache: '0' } });
  }

  /** The sources a report in this project can be pointed at: its own, plus the client's others. */
  getDataSources(projectId: string): Observable<EndClientReportDataSource[]> {
    return this.http.get<EndClientReportDataSource[]>(
      this.baseUrl + 'project/' + projectId + '/datasources', { headers: { cache: '0' } });
  }

  /**
   * Points one report at a different source. Per report, unlike a refresh: the others carry on
   * reading whatever they read. Returns the report, whose data state and "as of" stamp both change
   * with it -- the deck still has to re-fetch its records and datamap, which resolve through the
   * new source.
   */
  setDataSource(reportId: string, dataSourceId: string): Observable<EndClientReport> {
    return this.http.put<EndClientReport>(
      this.baseUrl + reportId + '/datasource', { dataSourceId });
  }

  /**
   * Source-scoped: every report reading this source gets the new numbers, not just the deck the
   * button was pressed in. `includeDatamap` also re-pulls the survey structure, which those decks
   * resolve their variables against -- opt-in for that reason, and the default is data alone.
   */
  refreshData(dataSourceId: string, includeDatamap = false): Observable<EndClientReportDataSource> {
    return this.http.post<EndClientReportDataSource>(
      this.baseUrl + 'datasource/' + dataSourceId + '/refresh-data', { includeDatamap })
      .pipe(tap(() => this.deckCache.forgetSource(dataSourceId)));
  }

  /**
   * The cheap poll while a run is in flight -- one row, no joins, no S3. It only answers "still
   * going, or worth pulling the full thing again?"; once it lands, re-fetch the report itself for
   * everything else (its status, version and counts all come from there).
   */
  getDataSourceStatus(dataSourceId: string): Observable<EndClientReportDataSource> {
    return this.http.get<EndClientReportDataSource>(
      this.baseUrl + 'datasource/' + dataSourceId + '/status', { headers: { cache: '0' } });
  }

  /**
   * Every domain in the instance, for the admin-only management screen (NK-399). Not the list the
   * deploy dialog reads — that one is client-scoped and needs no admin, since choosing a domain to
   * publish on is part of the reporting workflow rather than an administrative act.
   */
  getDomains(): Observable<EndClientReportDomain[]> {
    return this.http.get<EndClientReportDomain[]>(this.domainUrl, { headers: { cache: '0' } });
  }

  createDomain(domain: Partial<EndClientReportDomain>): Observable<EndClientReportDomain> {
    return this.http.post<EndClientReportDomain>(this.domainUrl, domain);
  }

  updateDomain(id: string, domain: Partial<EndClientReportDomain>): Observable<EndClientReportDomain> {
    return this.http.put<EndClientReportDomain>(this.domainUrl + id, domain);
  }

  /**
   * The domains this user's client may publish on: their own, plus the shared fallback. Needs no
   * admin, unlike managing the list -- choosing where to publish is part of the reporting workflow.
   */
  getDomainsForSharing(): Observable<EndClientReportDomain[]> {
    return this.http.get<EndClientReportDomain[]>(this.sharingUrl + 'domains', { headers: { cache: '0' } });
  }

  /**
   * A report's share link, or null when it has never been published. The API answers 204 for that
   * case, which arrives here as null rather than as an error.
   */
  getDeployment(reportId: string): Observable<EndClientReportDeployment | null> {
    return this.http.get<EndClientReportDeployment>(
      this.sharingUrl + 'report/' + reportId, { headers: { cache: '0' } });
  }

  /**
   * Publishes, or re-publishes onto a different domain or expiry. Returns with the deployment in
   * Building -- the bundle is built after the response, so the caller polls.
   */
  deployReport(
    reportId: string, domainId: string, expiresAt: string | null, unresolved: string[],
  ): Observable<EndClientReportDeployment> {
    return this.http.post<EndClientReportDeployment>(
      this.sharingUrl + 'report/' + reportId, { domainId, expiresAt, unresolved });
  }

  /** Rebuilds from the deck as it stands now. The published bundle keeps serving until it succeeds. */
  redeployReport(reportId: string, unresolved: string[]): Observable<EndClientReportDeployment> {
    return this.http.post<EndClientReportDeployment>(
      this.sharingUrl + 'report/' + reportId + '/redeploy', { unresolved });
  }

  /** Issues a new key. Every link already sent stops working. */
  rekeyReport(reportId: string): Observable<EndClientReportDeployment> {
    return this.http.post<EndClientReportDeployment>(this.sharingUrl + 'report/' + reportId + '/rekey', {});
  }

  setDeploymentExpiry(reportId: string, expiresAt: string | null): Observable<EndClientReportDeployment> {
    return this.http.put<EndClientReportDeployment>(
      this.sharingUrl + 'report/' + reportId + '/expiry', { expiresAt });
  }

  /** Takes the link down. The row stays, so a returning visitor is told rather than 404'd. */
  revokeReport(reportId: string): Observable<EndClientReportDeployment> {
    return this.http.post<EndClientReportDeployment>(this.sharingUrl + 'report/' + reportId + '/revoke', {});
  }
}
